import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/Button.js";
import { LanguageSelector } from "../components/LanguageSelector.js";

export function Landing() {
  const { t } = useTranslation();

  const features = [
    { num: "01", title: t("landing.features.spoiler.title"), body: t("landing.features.spoiler.body") },
    { num: "02", title: t("landing.features.progress.title"), body: t("landing.features.progress.body") },
    { num: "03", title: t("landing.features.polls.title"), body: t("landing.features.polls.body") },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--color-background)" }}>
      {/* Nav */}
      <nav
        className="flex items-center justify-between px-6 py-5 md:px-12"
        style={{ animation: "fade-in 0.5s ease-out both" }}
      >
        <span className="font-serif text-lg font-semibold tracking-tight" style={{ color: "var(--color-foreground)" }}>
          {t("nav.logo")}
        </span>
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <Link to="/login">
            <Button variant="ghost" size="sm">{t("landing.signIn")}</Button>
          </Link>
          <Link to="/signup">
            <Button size="sm">{t("landing.getStarted")}</Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <main className="flex-1 flex flex-col">
        <section className="relative px-6 pt-16 pb-24 md:px-12 md:pt-24 md:pb-32 overflow-hidden">
          {/* Decorative large italic background word */}
          <div
            aria-hidden
            className="pointer-events-none select-none absolute -right-8 top-4 font-serif italic leading-none"
            style={{
              fontSize: "clamp(8rem, 22vw, 20rem)",
              color: "var(--color-accent)",
              opacity: 0.035,
              fontWeight: 600,
              letterSpacing: "-0.04em",
              whiteSpace: "nowrap",
            }}
          >
            Read
          </div>

          {/* Top rule */}
          <div
            aria-hidden
            className="absolute left-0 right-0 top-0 h-px opacity-40"
            style={{ background: "var(--color-border-strong)" }}
          />

          <div className="relative max-w-3xl">
            {/* Chapter marker */}
            <div
              className="mb-6 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
              style={{
                color: "var(--color-accent)",
                animation: "slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both",
              }}
            >
              <span className="inline-block w-8 h-px" style={{ background: "var(--color-accent)" }} />
              {t("landing.hero.title")} {t("landing.hero.titleAccent")}
            </div>

            <h1
              className="font-serif font-semibold"
              style={{
                fontSize: "clamp(2.75rem, 6vw, 5.5rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                color: "var(--color-foreground)",
                textWrap: "balance",
                animation: "slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both",
              }}
            >
              {t("landing.hero.tagline").split(". ")[0]}.
              <br />
              <em style={{ color: "var(--color-accent)" }}>{t("landing.hero.tagline").split(". ")[1]}</em>
            </h1>

            <p
              className="mt-6 max-w-xl"
              style={{
                fontSize: "1.125rem",
                lineHeight: 1.65,
                color: "var(--color-muted-foreground)",
                textWrap: "balance",
                animation: "slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.25s both",
              }}
            >
              {t("landing.features.spoiler.body")}
            </p>

            <div
              className="mt-10 flex flex-wrap items-center gap-3"
              style={{ animation: "slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.35s both" }}
            >
              <Link to="/signup">
                <Button size="lg" className="px-8">{t("landing.hero.cta")}</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg">{t("landing.signIn")}</Button>
              </Link>
            </div>

            <p
              className="mt-5 text-xs"
              style={{
                color: "var(--color-muted-foreground)",
                animation: "fade-in 0.6s ease-out 0.5s both",
              }}
            >
              {t("landing.hero.subCta")}
            </p>
          </div>
        </section>

        {/* Section divider */}
        <div className="flex items-center gap-4 px-6 md:px-12" aria-hidden>
          <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
          <span className="font-serif italic text-lg" style={{ color: "var(--color-accent)", opacity: 0.4 }}>§</span>
          <div className="flex-1 h-px" style={{ background: "var(--color-border)" }} />
        </div>

        {/* Features */}
        <section className="px-6 py-20 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 gap-0 md:grid-cols-3">
              {features.map((f, i) => (
                <div
                  key={f.num}
                  className="px-6 py-8 md:px-8"
                  style={{
                    borderLeft: i > 0 ? "1px solid var(--color-border)" : undefined,
                    animation: `slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.05 + i * 0.1}s both`,
                  }}
                >
                  <div className="font-serif italic mb-4 text-xs font-medium" style={{ color: "var(--color-accent)" }}>
                    {f.num}
                  </div>
                  <h3 className="font-serif font-semibold mb-3 text-lg" style={{ color: "var(--color-foreground)" }}>
                    {f.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--color-muted-foreground)" }}>
                    {f.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section
          className="px-6 py-20 md:px-12 text-center"
          style={{ borderTop: "1px solid var(--color-border)" }}
        >
          <p className="font-serif italic mb-6 text-xl" style={{ color: "var(--color-muted-foreground)" }}>
            "The book you read alone stays with you.<br />
            The book you read together changes you."
          </p>
          <Link to="/signup">
            <Button size="lg" className="px-10">{t("landing.finalCta")}</Button>
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer
        className="px-6 py-5 md:px-12 flex items-center justify-between text-xs"
        style={{ color: "var(--color-muted-foreground)", borderTop: "1px solid var(--color-border)" }}
      >
        <span className="font-serif italic">{t("nav.logo")}</span>
        <span>{t("landing.footer")}</span>
      </footer>
    </div>
  );
}
